/**
 * Logto CIAM Service for LEXE Admin Panel
 *
 * Ported from webchat — uses SessionLogtoClient for session-based auth.
 */

import { LogtoConfig, PersistKey } from "@logto/react";
import LogtoClient from "@logto/browser";
import type { Nullable } from "@silverhand/essentials";

// ============================================================================
// Session Storage Implementation
// ============================================================================

class SessionBrowserStorage {
  readonly appId: string;

  constructor(appId: string) {
    this.appId = appId;
  }

  getKey(item?: string): string {
    return item ? `logto:${this.appId}:${item}` : `logto:${this.appId}`;
  }

  async getItem(key: PersistKey): Promise<Nullable<string>> {
    return sessionStorage.getItem(this.getKey(key));
  }

  async setItem(key: PersistKey, value: string): Promise<void> {
    sessionStorage.setItem(this.getKey(key), value);
  }

  async removeItem(key: PersistKey): Promise<void> {
    sessionStorage.removeItem(this.getKey(key));
  }
}

export class SessionLogtoClient extends LogtoClient {
  constructor(config: LogtoConfig, unstable_enableCache?: boolean) {
    const storage = new SessionBrowserStorage(config.appId);
    super(config, unstable_enableCache);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).adapter.storage = storage;
  }
}

// ============================================================================
// Configuration
// ============================================================================

export function isLogtoEnabled(): boolean {
  const endpoint = import.meta.env.VITE_LOGTO_ENDPOINT;
  const appId = import.meta.env.VITE_LOGTO_APP_ID;
  return Boolean(endpoint && appId);
}

export function getLogtoConfig(): LogtoConfig {
  const endpoint = import.meta.env.VITE_LOGTO_ENDPOINT;
  const appId = import.meta.env.VITE_LOGTO_APP_ID;
  const resource = import.meta.env.VITE_LOGTO_RESOURCE;

  if (!endpoint || !appId) {
    throw new Error(
      "Logto configuration missing. Set VITE_LOGTO_ENDPOINT and VITE_LOGTO_APP_ID",
    );
  }

  return {
    endpoint,
    appId,
    resources: resource ? [resource] : undefined,
    scopes: [
      "openid",
      "profile",
      "email",
      "urn:logto:scope:organizations",
      "urn:logto:scope:organization_roles",
    ],
  };
}
